import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSetAtom, useAtomValue, useAtom } from 'jotai';
import { Avatar, useTheme, Box } from '@mui/material';
import useAPI from '../../api';
import SectionPage from '../../Components/Paper/SectionPage';
import ConfirmPopup from '../../Components/Popup/ConfirmPopup';
import UserDialog from './Components/UserDialog';
import {
  selectedBranchAtom,
  openSnackbarAdviseAtom,
  searchValuesAtom,
} from '../../Atoms';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import ErrorDialog from '../../Components/Error';
import getErrorMessage from '../../Helpers/Errors/getErrorMessage';
import { DEFAULT_SERVICE, ROLES_ICONS, USERS_SORTING } from './Utils/constants';
import UsersFilters from './Components/UsersFilters';
import sortByProperty from '../../Helpers/Arrays/sortByProperty';
import OptionsMenu from './Components/OptionsMenu';
import ConfirmDialog from '../../Components/Dialog/ConfirmDialog';
import { CenteredFlexBox } from '../../Components/FlexBox';
import StatusChip from '../../Components/Chip/StatusChip';
import IconButton from '../../Components/Button/IconButton';
import keysToLowerCase from '../../Helpers/keysToLowerCase';

const UserManagementPage = function UserManagementPage({ heightQuery = true }) {
  const intl = useIntl();
  const API = useAPI();
  const theme = useTheme();

  const selectedBranch = useAtomValue(selectedBranchAtom);
  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);

  const [searchValues, setSearchValues] = useAtom(searchValuesAtom);
  const [usrsError, setUsrsError] = useState(null);
  const [userDialog, setUserDialog] = useState(null);
  const [optionsMenu, setOptionsMenu] = useState(null);
  const [confirmClosing, setConfirmClosing] = useState(null);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [confirmDisable, setConfirmDisable] = useState(null);

  //reads the filters saved in localStorage once, normalizing the
  //"undefined"/"null" string artifacts left by setItem(null/undefined)
  const persistedFilters = useMemo(() => {
    const getStored = (key) => {
      const value = localStorage.getItem(key);
      return value && value !== 'undefined' && value !== 'null' ? value : null;
    };
    const roleId = getStored('roleId');
    const userStatus = getStored('userStatus');
    const withVoipDevice = getStored('withVoipDevice');
    return {
      roleId,
      userStatus: userStatus !== null ? JSON.parse(userStatus) : null,
      withVoipDevice:
        withVoipDevice !== null ? JSON.parse(withVoipDevice) : null,
    };
  }, []);

  const columns = [
    {
      id: 'userName',
      noCaption: true,
      size: { md: 0.5 },
      formatCallback: (value) => (
        <Avatar
          sx={{
            color: 'inherit',
            backgroundColor: value
              ? theme.custom.stringToColor(value)
              : 'primary.main',
          }}
        >
          {value.length > 0 ? value[0]?.toUpperCase() : null}
        </Avatar>
      ),
    },
    {
      id: 'userName',
      size: { md: 1 },
    },
    {
      id: 'name',
      formatCallback: (value, data) => `${value} ${data?.surname}`,
    },
    /*     {
      id: 'createdOn',
      label: intl.messages.CREATION_DATE,
      formatCallback: (value) =>
        value ? new Date(value).toLocaleDateString(culture, options) : null,
    }, */
    {
      id: 'extensionData',
      label: intl.messages.CALLS_TARGET,
      size: 1.2,
      formatCallback: (value) =>
        keysToLowerCase(JSON.parse(value || '{}'))?.target,
    },
    {
      id: 'email',
      label: intl.messages.EMAIL,
    },
    {
      id: 'voIPDevice',
      label: intl.messages.DEVICE,
    },
    {
      id: 'deleted',
      size: { md: 1 },
      formatCallback: (value) => (
        <StatusChip
          status={value ? 'INACTIVE' : 'ACTIVE'}
          options={{
            ACTIVE: CheckCircleOutlineRoundedIcon,
            INACTIVE: HighlightOffRoundedIcon,
          }}
          colors={{ ACTIVE: '#34A853', INACTIVE: '#b00020' }}
        />
      ),
    },
  ];

  const renderChip = (data, onClick) => {
    return (
      <CenteredFlexBox gap="16px">
        {data?.roles?.map(({ roleId }) => (
          <Box
            key={roleId}
            sx={{
              width: '160px',
            }}
          >
            <StatusChip status={roleId?.toLowerCase()} options={ROLES_ICONS} />
          </Box>
        ))}
        <IconButton
          id="options-user-button"
          onClick={(e) => onClick(e, data)}
          size="large"
        >
          <MoreVertRoundedIcon />
        </IconButton>
      </CenteredFlexBox>
    );
  };

  /**onChange states functions**/
  const openNewUserDialog = () => {
    setUserDialog({ dialogType: 'NEW' });
  };

  const openEditUserDialog = (userData) => {
    setUserDialog({ ...userData, dialogType: 'EDIT' });
  };

  const openOptionsMenu = (event, data) => {
    setOptionsMenu({ anchor: event.currentTarget, data });
  };

  const handleChangeRoleFilter = (newRole, status, withVoipDevice) => {
    setSearchValues({
      ...searchValues,
      roleId: newRole || null,
      deleted: status !== null ? !status : null,
      //store null (not false) when the toggle is off so it is not counted
      //as an active filter by the filters badge
      withVoipDevice: withVoipDevice,
    });

    localStorage.setItem('roleId', newRole);
    localStorage.setItem('userStatus', status);
    localStorage.setItem('withVoipDevice', withVoipDevice);
  };
  /****/

  //users loading api
  const loadUserBranches = () => {
    if (selectedBranch?.id)
      API.getBranchUsers(
        selectedBranch?.id,
        (res) => {
          setUsers(
            res.filter(
              (x) => !x?.id.includes('00000000-0000-0000-0000-000000000000')
            )
          );
        },
        (err) => {
          setUsrsError(err);
        }
      );
  };

  /**API CALLS**/
  useEffect(() => {
    loadUserBranches(); //loads user branches
  }, [selectedBranch?.id]);

  //applies the persisted filters to searchValues on mount, so both the user
  //list and the active-filters badge reflect the saved selection (EntriesWrapper
  //resets searchValues on mount and is unaware of these custom filter keys)
  useEffect(() => {
    setSearchValues((prev) => ({
      ...prev,
      roleId: persistedFilters.roleId,
      deleted:
        persistedFilters.userStatus !== null
          ? !persistedFilters.userStatus
          : null,
      withVoipDevice: persistedFilters.withVoipDevice,
    }));
  }, [persistedFilters, setSearchValues]);

  useEffect(() => {
    API.getRoles(
      (res) => {
        setRoles(res);
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.ROLES_LOADING_ERROR,
          severity: 'error',
        });
      }
    );
  }, []);

  useEffect(() => {
    API.getAllBranches(
      (res) => {
        setAllBranches(res);
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.BRANCHES_LOADING_ERROR,
          severity: 'error',
        });
      }
    );
  }, []);
  /****/

  /**USER MANAGEMENT CALLS**/
  const handleAddUser = (payload) => {
    API.registerUser(
      payload,
      ({ id }) => {
        API.addUserToBranch(
          id,
          payload?.branches,
          false, //inEdit
          () => {
            setUserDialog(null);
            setOptionsMenu(null);
            loadUserBranches();
            setOpenSnackbarAdvise({
              message: intl.messages.USER_CREATED_SUCCESSFULLY,
              severity: 'success',
            });
          },
          (err) => {
            setOpenSnackbarAdvise({
              message: `${intl.messages.CREATE_USER_ERROR}: ${getErrorMessage(
                err
              )}`,
              severity: 'error',
            });
          }
        );
      },
      (err) => {
        setOpenSnackbarAdvise({
          message: `${intl.messages.CREATE_USER_ERROR}: ${getErrorMessage(
            err
          )}`,
          severity: 'error',
        });
      }
    );
  };

  const handleEditUser = (payload, userId) => {
    API.updateUser(
      userId,
      payload,
      () => {
        API.addUserToBranch(
          userId,
          payload?.branches,
          true, //inEdit
          () => {
            setUserDialog(null);
            setOptionsMenu(null);
            loadUserBranches();
            setOpenSnackbarAdvise({
              message: intl.messages.USER_EDITED_SUCCESSFULLY,
              severity: 'success',
            });
          },
          (err) => {
            setOpenSnackbarAdvise({
              message: `${intl.messages.EDIT_USER_ERROR}: ${getErrorMessage(
                err
              )}`,
              severity: 'error',
            });
          }
        );
      },
      (err) => {
        setOpenSnackbarAdvise({
          message: `${intl.messages.EDIT_USER_ERROR}: ${getErrorMessage(err)}`,
          severity: 'error',
        });
      }
    );
  };

  const handleSubmit = (submitData) => {
    const inEdit = submitData.dialogType === 'EDIT';
    const payload = {
      ...submitData,
      serviceId: DEFAULT_SERVICE,

      phoneNumber: submitData?.phoneNumber
        ? submitData?.phoneNumber?.startsWith('+39')
          ? submitData?.phoneNumber //phone number submitted with +39
          : `+39${submitData?.phoneNumber}` //phone number submitted without +39
        : null, //phone number submitted null
      extensionData: JSON.stringify(submitData?.extensionData),
    };
    delete payload.dialogType;
    if (!inEdit) {
      handleAddUser(payload);
    } else if (payload?.id) {
      handleEditUser(payload, payload?.id);
    } else {
      setOpenSnackbarAdvise({
        message: intl.messages.USER_NOT_FOUND,
        severity: 'error',
      });
    }
  };

  const handleOpenConfirmDisable = (userData) => {
    setConfirmDisable(userData);
  };
  const handleCloseConfirmDisable = () => {
    setConfirmDisable(null);
  };

  const handleDisableUser = (user) => {
    API.disableUser(
      user?.id,
      () => {
        loadUserBranches();
        handleCloseConfirmDisable();
        setOptionsMenu(null);
        setOpenSnackbarAdvise({
          message: intl.messages.USER_DISABLED,
          severity: 'success',
        });
      },
      (err) => {
        setOpenSnackbarAdvise({
          message:
            err?.payload?.errors[0]?.description ||
            intl.messages.ERROR_IN_DISABLING,
          severity: 'error',
        });
      }
    );
  };
  /****/

  //applies filters to users array
  const filteredAndSortedUsers = useMemo(() => {
    if (users && users.length > 0) {
      let filteredUsers = [...users];
      if (searchValues?.searchBox) {
        filteredUsers = filteredUsers.filter(
          ({ name, surname }) =>
            name
              ?.toUpperCase()
              ?.includes(searchValues?.searchBox?.toUpperCase()) ||
            surname
              ?.toUpperCase()
              ?.includes(searchValues?.searchBox?.toUpperCase())
        );
      }
      if (searchValues?.roleId) {
        filteredUsers = filteredUsers.filter((item) =>
          item?.roles.find(
            ({ roleId }) => roleId?.toUpperCase() === searchValues?.roleId
          )
        );
      }
      if (
        searchValues?.deleted !== undefined &&
        searchValues?.deleted !== null
      ) {
        filteredUsers = filteredUsers.filter(
          ({ deleted }) => deleted === searchValues?.deleted
        );
      }
      if (
        searchValues?.withVoipDevice !== undefined &&
        searchValues?.withVoipDevice !== null
      ) {
        filteredUsers = filteredUsers.filter(({ voIPDevice }) =>
          searchValues?.withVoipDevice ? voIPDevice : !voIPDevice
        );
      }
      return sortByProperty(
        filteredUsers,
        searchValues?.sortValue?.value,
        searchValues?.sortValue?.direction === 'asc'
          ? 'ASCENDING'
          : 'DESCENDING'
      );
    }
    return [];
  }, [searchValues, users]);

  return (
    <>
      <ErrorDialog error={usrsError} />
      <SectionPage
        disableClickRow
        hideExportExcel
        sectionName="USER_MANAGEMENT"
        sectionSubName="USERS"
        fab={intl.messages.NEW_USER}
        onClickFab={openNewUserDialog}
        fabVariant="extended"
        columns={columns}
        singlePageList={filteredAndSortedUsers}
        customStatusChipCallback={({ data }) =>
          renderChip(data, openOptionsMenu)
        }
        customFilters={
          <UsersFilters
            //derive the dialog defaults from the live searchValues so the dialog
            //always mirrors the currently applied filters (incl. after a clear)
            defaultValues={{
              roleId: searchValues?.roleId || null,
              userStatus:
                searchValues?.deleted !== undefined &&
                searchValues?.deleted !== null
                  ? !searchValues?.deleted
                  : null,
              withVoipDevice: searchValues?.withVoipDevice,
            }}
            onChangeFilters={handleChangeRoleFilter}
          />
        }
        sortingOptions={USERS_SORTING}
      />
      <OptionsMenu
        anchor={optionsMenu?.anchor}
        data={optionsMenu?.data}
        disableDelete={optionsMenu?.data?.deleted}
        onEdit={openEditUserDialog}
        onDelete={handleOpenConfirmDisable}
        onClose={() => setOptionsMenu(null)}
      />
      <UserDialog
        branches={allBranches}
        roles={roles}
        onSubmit={handleSubmit}
        heightQuery={heightQuery}
        open={Boolean(userDialog)}
        userData={userDialog}
        edit={userDialog?.dialogType === 'EDIT'}
        close={(e, isFormDirty) => {
          //isFormDirty is a boolean that checks if the user has changed at least one fields values
          if (isFormDirty) {
            setConfirmClosing(e.currentTarget);
            if (userDialog?.dialogType === 'EDIT') {
              setOptionsMenu(null); //closes options menu
            }
          } else {
            setUserDialog(null); //if no fields touched the dialog close immediately without confirmation
            if (userDialog?.dialogType === 'EDIT') {
              setOptionsMenu(null); //closes options menu
            }
          }
        }}
      />
      <ConfirmPopup
        confirmText={intl.messages.CLOSE_WINDOW}
        open={Boolean(confirmClosing)}
        anchorEl={confirmClosing}
        close={() => setConfirmClosing(null)}
        handleConfirm={() => {
          setConfirmClosing(null);
          setUserDialog(null);
        }}
      />
      <ConfirmDialog
        open={Boolean(confirmDisable)}
        title="DISABLE_USER"
        question="DISABLE_USER_QUESTION"
        onCancel={handleCloseConfirmDisable}
        onSubmit={() => handleDisableUser(confirmDisable)}
      />
    </>
  );
};

UserManagementPage.propTypes = {
  heightQuery: PropTypes.bool,
};

export default UserManagementPage;
